import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdvertisementDto, AdvertisingTeamRollupRowDto } from "@/api/advertising";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  EmptyState,
  MissingValue,
  Skeleton,
  StatCard,
  StatsBar,
  StatusBadge,
  SubTable,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  useAdvertisements,
  useAdvertisingBudgets,
  useAdvertisingCostPerLead,
  useAdvertisingRollup,
  useAdvertisingScope,
} from "@/hooks/buyers/useAdvertising";
import { useAuth } from "@/hooks/useAuth";
import { QueryErrorState } from "../queryStates";
import { AdvertisementEditorModal } from "./AdvertisementEditorModal";
import { AdvertisementsTable } from "./AdvertisementsTable";
import { BudgetEditorModal } from "./BudgetEditorModal";
import { SpendLedgerModal } from "./SpendLedgerModal";
import {
  CostCell,
  currentPeriod,
  money,
  OverBudgetBadge,
  periodLabel,
  shiftPeriod,
  utilizationLabel,
} from "./advertisingBits";

const READ_PERMISSION = "BuyerAdvertising.Read";

/**
 * Buyer advertising (WS6) — what a team is allowed to spend, what it has spent,
 * and what that bought.
 *
 * <p>🔴 <b>Every figure on this page is MANAGEMENT REPORTING.</b> It reaches no
 * income event, no waterfall, no disbursement, no commission and no 1099.
 * Advertising money leaves through somebody else's rails; this is where it is
 * written down so it can be measured against what it produced.</p>
 *
 * <p><b>Scope comes from the server.</b> Which teams appear, which are writable
 * and where the page lands are all resolved by the scope endpoint rather than
 * derived here from a permission list — two implementations of one rule is two
 * answers the first time either moves, and the visible failure would be a
 * button that 403s.</p>
 */
export function BuyerAdvertisingPage() {
  const { hasPermission } = useAuth();
  const canRead = hasPermission(READ_PERMISSION);

  // Team and month are URL-persisted so a manager can send somebody the exact
  // view they are looking at, and so a refresh does not lose it. Defaults are
  // resolved at PARSE time, never in a post-mount effect — an effect that
  // rewrites the search params re-keys the queries and double-fetches the page.
  const [params, setParams] = useSearchParams();
  const teamParam = params.get("team");
  const period = params.get("period") ?? currentPeriod();

  const scope = useAdvertisingScope(canRead);

  const teamId = teamParam ?? scope.data?.defaultTeamId ?? null;
  const team = scope.data?.teams.find((t) => t.id === teamId) ?? null;
  const canManageThisTeam = team?.canManage === true && scope.data?.canManageAds === true;
  const canManageBudget = team?.canManage === true && scope.data?.canManageBudget === true;

  const ads = useAdvertisements(teamId);
  const budgets = useAdvertisingBudgets(teamId);
  const rollup = useAdvertisingRollup(period, scope.data?.canManageAllTeams === true);
  // The month's spend for THIS team, from the cost-per-lead report — the one
  // month-bounded read a one-team manager may make (the roll-up is a
  // Buyer Director view and `spendToDate` is lifetime).
  const pace = useAdvertisingCostPerLead(
    { from: period, to: period, buyerTeamId: teamId },
    { enabled: canRead && !!teamId && !!team },
  );
  const paceRow = pace.data?.budgets.find((b) => b.buyerTeamId === teamId) ?? null;

  const budget = useMemo(
    () => budgets.data?.find((b) => b.period === period) ?? null,
    [budgets.data, period],
  );

  const [editing, setEditing] = useState<AdvertisementDto | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [spendFor, setSpendFor] = useState<AdvertisementDto | null>(null);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  }

  // The month's figures come from the pace row; the roll-up row (Director
  // only) carries the server's own utilization when present. Lifetime spend
  // is the sum of the advertisements' lifetime totals — a DIFFERENT number,
  // labelled as such rather than quietly presented as the month's.
  const rollupRow = rollup.data?.teams.find((t) => t.buyerTeamId === teamId) ?? null;
  const monthSpend = paceRow?.spend ?? rollupRow?.spendToDate ?? null;
  const monthUsed =
    utilizationLabel(rollupRow?.utilizationFraction)
    ?? (paceRow && paceRow.budget ? utilizationLabel(paceRow.spend / paceRow.budget) : null);
  const lifetimeSpend = (ads.data ?? []).reduce((sum, a) => sum + a.metrics.spendToDate, 0);
  const lifetimeLeads = (ads.data ?? []).reduce((sum, a) => sum + a.metrics.leads, 0);

  const rollupColumns: SubTableColumn<AdvertisingTeamRollupRowDto>[] = [
    { key: "team", header: "Team", render: (r) => r.teamName, sortAccessor: (r) => r.teamName },
    {
      key: "budget",
      header: "Budget",
      align: "right",
      render: (r) => (r.budget === null || r.budget === undefined
        ? <MissingValue />
        : money(r.budget)),
    },
    {
      key: "spend",
      header: "Spend",
      align: "right",
      render: (r) => money(r.spendToDate),
      sortAccessor: (r) => r.spendToDate,
    },
    {
      key: "util",
      header: "Used",
      align: "right",
      render: (r) => {
        const label = utilizationLabel(r.utilizationFraction);
        if (label === null) return <MissingValue />;
        // A flag, never a block — the spend already happened.
        return r.isOverBudget
          ? <StatusBadge tone="danger">{label}</StatusBadge>
          : <span>{label}</span>;
      },
      sortAccessor: (r) => r.utilizationFraction ?? -1,
    },
    { key: "leads", header: "Leads", align: "right", render: (r) => r.leads },
    { key: "qualified", header: "Qualified", align: "right", render: (r) => r.qualified },
    {
      key: "cpl",
      header: "Cost / lead",
      align: "right",
      render: (r) => <CostCell value={r.costPerLead} />,
    },
  ];

  if (!canRead) {
    return (
      <EmptyState
        title="Not available"
        description="You do not have access to buyer advertising."
      />
    );
  }

  return (
    <div className="space-y-space-5">
      <PageHeader
        eyebrow="Buyers"
        title="Advertising"
        description="What each buyer team is allowed to spend, what it has spent, and what that bought. Reporting only — none of it moves money."
        actions={
          <div className="flex flex-wrap items-end gap-space-2">
            <div className="flex items-center gap-space-1">
              <Button
                variant="secondary"
                onClick={() => setParam("period", shiftPeriod(period, -1))}
                aria-label="Previous month"
              >
                ‹
              </Button>
              <span className="min-w-[9rem] text-center font-medium text-onyx">
                {periodLabel(period)}
              </span>
              <Button
                variant="secondary"
                onClick={() => setParam("period", shiftPeriod(period, 1))}
                aria-label="Next month"
              >
                ›
              </Button>
            </div>

            {canManageBudget && teamId && (
              <Button variant="secondary" onClick={() => setBudgetOpen(true)}>
                {budget ? "Edit budget" : "Set budget"}
              </Button>
            )}
            {canManageThisTeam && teamId && (
              <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
                New advertisement
              </Button>
            )}
          </div>
        }
      />

      {scope.isError && (
        <QueryErrorState
          what="your advertising scope"
          onRetry={() => void scope.refetch()}
          retrying={scope.isFetching}
        />
      )}

      {scope.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (scope.data?.teams.length ?? 0) === 0 ? (
        <EmptyState
          title="No team to show"
          description="Advertising is scoped to a buyer team. Ask a Buyer Director to add you to one."
        />
      ) : (
        <>
          {(scope.data?.teams.length ?? 0) > 1 && (
            <div className="max-w-sm">
              <Select
                aria-label="Buyer team"
                value={teamId ?? ""}
                onChange={(e) => setParam("team", e.target.value)}
              >
                {scope.data?.teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
          )}

          <StatsBar>
            <StatCard
              label={`${periodLabel(period)} budget`}
              value={budget ? money(budget.amount) : "—"}
            />
            <StatCard
              label={`${periodLabel(period)} spend`}
              value={monthSpend === null ? "—" : money(monthSpend)}
            />
            <StatCard
              label={`${periodLabel(period)} used`}
              value={monthUsed ?? "—"}
            />
            <StatCard label="Lifetime spend" value={money(lifetimeSpend)} />
            <StatCard label="Lifetime leads" value={lifetimeLeads} />
          </StatsBar>

          {/* A flag, never a block — the spend already happened. Nothing
              renders under budget: state-absent is prose, not a pill. */}
          <OverBudgetBadge pace={paceRow} />

          {pace.isError && (
            <p className="text-caption text-gray-mid">
              Month-to-date spend could not be loaded. The lifetime figures
              beside it are this team's own and are always available.
            </p>
          )}

          {budget?.setByDisplayName && (
            <p className="text-caption text-gray-mid">
              Budget set by {budget.setByDisplayName}
              {budget.modifiedAt && <> · <Timestamp value={budget.modifiedAt} /></>}
            </p>
          )}

          {ads.isError ? (
            <QueryErrorState
              what="this team's advertisements"
              onRetry={() => void ads.refetch()}
              retrying={ads.isFetching}
            />
          ) : ads.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            /* The SAME table a team's ADVERTISING tab renders — one set of
               columns and one empty copy, so the two surfaces cannot show two
               answers for one advertisement. */
            <AdvertisementsTable
              ads={ads.data ?? []}
              teamName={team?.name}
              canManage={canManageThisTeam}
              onSpend={setSpendFor}
              onEdit={(a) => { setEditing(a); setEditorOpen(true); }}
            />
          )}

          {scope.data?.canManageAllTeams && (
            <section className="space-y-space-2">
              <h2 className="font-display text-h3 text-onyx">
                Every team — {periodLabel(period)}
              </h2>
              {rollup.isError ? (
                <QueryErrorState
                  what="the cross-team roll-up"
                  onRetry={() => void rollup.refetch()}
                  retrying={rollup.isFetching}
                />
              ) : rollup.isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <>
                  <SubTable
                    caption={`Advertising roll-up for ${periodLabel(period)}`}
                    columns={rollupColumns}
                    rows={rollup.data?.teams ?? []}
                    rowKey={(r) => r.buyerTeamId}
                    empty={{
                      message:
                        "No team has a budget, any spend or any attributed lead this month.",
                    }}
                  />
                  <p className="text-caption text-gray-mid">
                    {money(rollup.data?.totalSpend ?? 0)} spent against{" "}
                    {money(rollup.data?.totalBudget ?? 0)} budgeted,{" "}
                    {rollup.data?.totalLeads ?? 0} attributed leads, blended cost
                    per lead{" "}
                    {rollup.data?.blendedCostPerLead === null
                      || rollup.data?.blendedCostPerLead === undefined
                      ? "—"
                      : money(rollup.data.blendedCostPerLead)}
                    .
                  </p>
                </>
              )}
            </section>
          )}
        </>
      )}

      {teamId && (
        <AdvertisementEditorModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          buyerTeamId={teamId}
          advertisement={editing}
          brands={scope.data?.brands ?? []}
        />
      )}

      {teamId && (
        <BudgetEditorModal
          open={budgetOpen}
          onClose={() => setBudgetOpen(false)}
          buyerTeamId={teamId}
          period={period}
          budget={budget}
        />
      )}

      {spendFor && (
        <SpendLedgerModal
          open={spendFor !== null}
          onClose={() => setSpendFor(null)}
          advertisement={spendFor}
          canRecord={canManageThisTeam}
          canCorrect={scope.data?.canManageAllTeams === true}
        />
      )}
    </div>
  );
}
